import EmployeesChart from "components/charts/modern/EmployeesChart";
import FreeFloatChart from "components/charts/modern/FreeFloatChart";
import {
  CompanyContainer,
  CompanyContentContainer,
  CompanyTabsContent,
  CompanyTabsList,
} from "components/company/v3/CompanyContainers";
import BrazilianGovernanceContent from "components/company/v3/brazilian/contents/governance/BrazilianGovernanceContent";
import BrazilianOfficersContent from "components/company/v3/brazilian/contents/governance/BrazilianOfficersContent";
import BrazilianTotalCompensationsContent from "components/company/v3/brazilian/contents/governance/BrazilianTotalCompensationsContent";
import useBrazilianIssuer from "components/company/v3/context/hooks/useBrazilianIssuer";
import { CompanyGovernanceTabEnum } from "components/company/v3/menu/enums";
import classNames from "components/ui/classNames";
import { Tabs } from "components/ui/Tabs";
import useSelfie from "lib/hooks/useSelfie";
import TickerSymbol from "lib/securities/TickerSymbol";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import { BriefcaseBusinessIcon, DollarSignIcon, PercentIcon, UserSearchIcon, UsersIcon } from "lucide-react";
import { useIntl } from "react-intl";
import type { Updater } from "react-powerplug";
import { Value } from "react-powerplug";

interface BrazilianGovernanceBoxProps {
  tickerSymbol: string;
  isRenderPageDefault?: boolean;
  tab: CompanyGovernanceTabEnum;
  onChangeTab: (tab: CompanyGovernanceTabEnum) => void;
}

export default function BrazilianGovernanceBox({
  tickerSymbol,
  isRenderPageDefault = false,
  tab,
  onChangeTab,
}: BrazilianGovernanceBoxProps) {
  const intl = useIntl();
  const issuer = useBrazilianIssuer();
  const viewer = useSelfie();

  const tickerSymbolsWithQuotes = issuer.securities.map((node) => node.tickerSymbol);
  const availableTickerSymbols = tickerSymbolsWithQuotes.filter((tickerSymbol) => {
    return !TickerSymbol.isUnitStockSymbol(tickerSymbol);
  });

  const hasAvailableTickerSymbols = availableTickerSymbols.length > 0;

  return (
    <CompanyContainer
      className={classNames({
        "xl:h-full": [
          CompanyGovernanceTabEnum.EQUITY_POSITION,
          CompanyGovernanceTabEnum.EXECUTIVE,
          CompanyGovernanceTabEnum.COMPENSATION,
        ].includes(tab),
      })}
    >
      <CompanyContentContainer>
        <Tabs
          defaultValue={CompanyGovernanceTabEnum.EQUITY_POSITION}
          value={tab}
          onValueChange={(value) => onChangeTab(value as CompanyGovernanceTabEnum)}
          className="h-full w-full"
        >
          <CompanyTabsList
            tabs={[
              {
                value: CompanyGovernanceTabEnum.EQUITY_POSITION,
                label: intl.formatMessage({
                  defaultMessage: "Posição Acionária",
                  description: "Aba do menu superior da empresa",
                  id: "ROJynb",
                }),
                icon: <UserSearchIcon className="inline-flex" size={16} strokeWidth={2} />,
              },
              viewer?.isPremium
                ? {
                    value: CompanyGovernanceTabEnum.FREE_FLOAT,
                    label: intl.formatMessage({
                      defaultMessage: "Free Float e Tag Along",
                      description: "Aba do menu superior da empresa",
                      id: "B3uqQ4",
                    }),
                    icon: <PercentIcon className="inline-flex" size={16} strokeWidth={2} />,
                    disabled: !hasAvailableTickerSymbols,
                  }
                : null,
              {
                value: CompanyGovernanceTabEnum.EXECUTIVE,
                label: intl.formatMessage({
                  defaultMessage: "Executivos",
                  description: "Aba do menu superior da empresa",
                  id: "DutxfM",
                }),
                icon: <UsersIcon className="inline-flex" size={16} strokeWidth={2} />,
              },
              {
                value: CompanyGovernanceTabEnum.COMPENSATION,
                label: intl.formatMessage({
                  defaultMessage: "Remuneração",
                  description: "Aba do menu superior da empresa",
                  id: "mD4xGc",
                }),
                icon: <DollarSignIcon className="inline-flex" size={16} strokeWidth={2} />,
              },
              {
                value: CompanyGovernanceTabEnum.EMPLOYEES,
                label: intl.formatMessage({
                  defaultMessage: "Empregados",
                  description: "Aba do menu superior da empresa",
                  id: "YuiKZP",
                }),
                icon: <BriefcaseBusinessIcon className="inline-flex" size={16} strokeWidth={2} />,
              },
            ].filter(notUndefinedOrNull)}
          />

          <CompanyTabsContent value={CompanyGovernanceTabEnum.EQUITY_POSITION}>
            <BrazilianGovernanceContent isRenderPageDefault={isRenderPageDefault} />
          </CompanyTabsContent>

          <CompanyTabsContent value={CompanyGovernanceTabEnum.FREE_FLOAT}>
            <Value<string[]>
              key={`FreeFloatChart/${availableTickerSymbols.join("")}`}
              initial={[availableTickerSymbols[0]]}
            >
              {({ value, set }) => (
                <FreeFloatChart
                  tickerSymbols={value}
                  availableTickerSymbols={availableTickerSymbols}
                  onToggleTickerSymbols={(tickerSymbols) => {
                    onToggleTickerSymbols(availableTickerSymbols[0], tickerSymbols, set);
                  }}
                />
              )}
            </Value>
          </CompanyTabsContent>

          <CompanyTabsContent value={CompanyGovernanceTabEnum.EXECUTIVE}>
            <BrazilianOfficersContent isRenderPageDefault={isRenderPageDefault} />
          </CompanyTabsContent>

          <CompanyTabsContent value={CompanyGovernanceTabEnum.COMPENSATION}>
            <BrazilianTotalCompensationsContent isRenderPageDefault={isRenderPageDefault} />
          </CompanyTabsContent>

          <CompanyTabsContent value={CompanyGovernanceTabEnum.EMPLOYEES}>
            <EmployeesChart tickerSymbols={[tickerSymbol]} />
          </CompanyTabsContent>
        </Tabs>
      </CompanyContentContainer>
    </CompanyContainer>
  );
}

function onToggleTickerSymbols(tickerSymbol: string, tickerSymbols: string[], set: Updater<string[]>) {
  if (tickerSymbols.length === 0) {
    set([tickerSymbol]);
  } else {
    set(tickerSymbols);
  }
}
